import React, { useEffect, useMemo, useState } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';

interface EdgeLexTemporalContext {
  schema: 'edgelex_temporal_context.v1';
  now_utc: string;
  firm_timezone: string;
  now_firm_local?: string;
  timezone_label?: string;
  clock_source?: string;
  timezone_source?: string;
  fallback_used?: boolean;
}

interface ClockState {
  temporal: EdgeLexTemporalContext | null;
  syncedAtMs: number;
  error: boolean;
}

const SYNC_INTERVAL_MS = 60_000;
const TICK_INTERVAL_MS = 1_000;

function formatClockSource(source?: string): string {
  if (source === 'postgres.clock_timestamp') return 'PostgreSQL clock_timestamp()';
  if (source === 'node.DateTime.utc') return 'Node UTC clock fallback';
  return source || 'unknown clock';
}

function formatTimezoneSource(source?: string): string {
  if (source === 'firms.firmSettings.timezone') return 'Firm Settings';
  if (source === 'system_preferences.timezone') return 'System Preferences';
  if (source === 'request.timezone') return 'request context';
  if (source === 'device.header') return 'device header';
  if (source === 'preserved_context.timezone') return 'saved user context';
  if (source === 'jwt.zoneinfo') return 'JWT token';
  if (source === 'default.utc') return 'UTC fallback';
  return source || 'unknown timezone source';
}

function formatOffset(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'shortOffset',
  }).formatToParts(date);
  return parts.find((part) => part.type === 'timeZoneName')?.value || timezone;
}

function formatAbbreviation(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  }).formatToParts(date);
  return parts.find((part) => part.type === 'timeZoneName')?.value || '';
}

const FirstClassClock: React.FC = () => {
  const [clockState, setClockState] = useState<ClockState>({
    temporal: null,
    syncedAtMs: Date.now(),
    error: false,
  });
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    let mounted = true;

    const syncClock = async () => {
      try {
        const token = localStorage.getItem('edgelex_access_token');
        const response = await fetch('/api/v1/time/current', {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error(`Time sync failed: ${response.status}`);
        const temporal = await response.json() as EdgeLexTemporalContext;
        if (!mounted) return;
        setClockState({
          temporal,
          syncedAtMs: Date.now(),
          error: false,
        });
      } catch {
        if (!mounted) return;
        setClockState((current) => ({
          ...current,
          error: true,
        }));
      }
    };

    void syncClock();
    const interval = window.setInterval(syncClock, SYNC_INTERVAL_MS);
    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), TICK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, []);

  const display = useMemo(() => {
    const temporal = clockState.temporal;
    const timezone = temporal?.firm_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const serverNowMs = temporal?.now_utc ? Date.parse(temporal.now_utc) : Number.NaN;
    const displayDate = Number.isFinite(serverNowMs)
      ? new Date(serverNowMs + (nowMs - clockState.syncedAtMs))
      : new Date(nowMs);

    const time = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(displayDate);
    const date = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(displayDate);
    const abbreviation = formatAbbreviation(displayDate, timezone);
    const offset = formatOffset(displayDate, timezone);
    return { time, date, abbreviation, offset, timezone };
  }, [clockState.syncedAtMs, clockState.temporal, nowMs]);

  const title = clockState.temporal
    ? [
        `EdgeLex Time Keeper`,
        `Clock: ${formatClockSource(clockState.temporal.clock_source)}`,
        `Timezone: ${clockState.temporal.firm_timezone}`,
        `Timezone source: ${formatTimezoneSource(clockState.temporal.timezone_source)}`,
        `UTC source time: ${clockState.temporal.now_utc}`,
        clockState.temporal.fallback_used ? 'Fallback used for part of this resolution.' : '',
      ].filter(Boolean).join('\n')
    : 'Syncing EdgeLex Time Keeper';

  return (
    <div
      className="hidden md:flex absolute left-1/2 top-1/2 h-8 w-[280px] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-2 rounded-md border border-[var(--lex-border)] bg-[var(--lex-surface-card)] px-3 text-[var(--lex-text-secondary)] shadow-sm"
      title={title}
      aria-label={`EdgeLex firm time ${display.time} ${display.abbreviation}`}
      data-testid="edgelex-first-class-clock"
    >
      <ClockIcon className="h-4 w-4 flex-shrink-0 text-[var(--lex-text-tertiary)]" aria-hidden="true" />
      <div className="flex min-w-0 items-baseline gap-2 tabular-nums">
        <span className="text-sm font-semibold text-[var(--lex-text-primary)]">{display.time}</span>
        <span className="text-xs text-[var(--lex-text-tertiary)]">{display.abbreviation || display.offset}</span>
        <span className="text-xs text-[var(--lex-text-tertiary)]">{display.date}</span>
      </div>
      {clockState.error && (
        <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" aria-label="Clock sync degraded" />
      )}
    </div>
  );
};

export default FirstClassClock;
